import {
  FeatureCategory,
  FeatureFlag,
  FeatureKey,
  FeatureOverrides,
  FeatureRegistry,
  FeatureOverrideValue,
  LEGACY_FEATURE_KEY_ALIASES,
} from './types';
import { FEATURE_REGISTRY } from './registry';

export const ENV_PREFIX = 'LOAMIFY_FEATURE_';

export function getFeature(
  key: FeatureKey,
  registry: FeatureRegistry = FEATURE_REGISTRY,
): FeatureFlag {
  const feature = registry[key];
  if (!feature) {
    throw new Error(`Feature flag is not registered: ${key}`);
  }
  return feature;
}

export function listFeatures(registry: FeatureRegistry = FEATURE_REGISTRY): FeatureFlag[] {
  return Object.values(registry);
}

export function listFeaturesByCategory(
  category: FeatureCategory,
  registry: FeatureRegistry = FEATURE_REGISTRY,
): FeatureFlag[] {
  return listFeatures(registry).filter((feature) => feature.category === category);
}

export function getFeatureEnvVarName(key: FeatureKey): string {
  return `${ENV_PREFIX}${key.toUpperCase()}`;
}

export function getOverrideValue(
  key: FeatureKey,
  overrides?: FeatureOverrides,
): FeatureOverrideValue {
  if (!overrides) {
    return undefined;
  }

  const direct = overrides[key] ?? overrides[getFeatureEnvVarName(key)];
  if (direct !== undefined) {
    return direct;
  }

  for (const [legacyKey, canonicalKey] of Object.entries(LEGACY_FEATURE_KEY_ALIASES)) {
    if (canonicalKey !== key) continue;
    const legacyValue =
      overrides[legacyKey as FeatureKey] ??
      overrides[getFeatureEnvVarName(legacyKey as FeatureKey)] ??
      overrides[`LOAMIFY_FEATURE_${legacyKey.toUpperCase()}`];
    if (legacyValue !== undefined) {
      return legacyValue;
    }
  }

  return undefined;
}

export function parseFeatureOverride(
  value: FeatureOverrideValue,
  fallback: boolean,
): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value !== 'string') {
    return fallback;
  }

  const normalized = value.trim().toLowerCase();

  if (['1', 'true', 'yes', 'on', 'enabled'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'off', 'disabled'].includes(normalized)) {
    return false;
  }

  return fallback;
}

type RuntimeEnv = Record<string, string | undefined>;

function getRuntimeEnv(): RuntimeEnv {
  const runtime = globalThis as typeof globalThis & {
    process?: { env?: RuntimeEnv };
  };

  return runtime.process?.env ?? {};
}

export function resolveEnvironmentOverrides(env: RuntimeEnv = getRuntimeEnv()): FeatureOverrides {
  return Object.values(FeatureKey).reduce<FeatureOverrides>((overrides, key) => {
    const envValue = getOverrideValue(key, env as FeatureOverrides);
    if (envValue !== undefined) {
      overrides[key] = envValue;
    }
    return overrides;
  }, {});
}

export function mergeFeatureOverrides(
  ...overridesList: Array<FeatureOverrides | undefined>
): FeatureOverrides {
  return overridesList.reduce<FeatureOverrides>(
    (merged, overrides) => ({ ...merged, ...(overrides ?? {}) }),
    {},
  );
}
